import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, LifeBuoy, Plus, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["Technical Issue", "Login / Authentication", "Payment / Subscription", "Enquiry / Event",
  "Seller Management", "Purchase Order", "Auction", "Approval", "Documents", "Other"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUS_STYLES = {
  Open: "bg-blue-100 text-blue-800", "In Progress": "bg-amber-100 text-amber-800",
  "Waiting for User": "bg-purple-100 text-purple-800", Resolved: "bg-green-100 text-green-800",
  Closed: "bg-slate-100 text-slate-600",
};
const PRIORITY_STYLES = { Low: "text-slate-500", Medium: "text-blue-600", High: "text-amber-600", Critical: "font-semibold text-red-600" };

export default function SupportPage() {
  const [tickets, setTickets] = useState(null);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "Medium", category: "Technical Issue" });
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/support/tickets").then((r) => setTickets(r.data.tickets)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const openTicket = (t) => api.get(`/support/tickets/${t.id}`).then((r) => setActive(r.data.ticket)).catch(() => {});

  const raise = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return toast.error("Issue title and description are required");
    setSaving(true);
    try {
      const r = await api.post("/support/tickets", form);
      toast.success(`Ticket ${r.data.ticket_number} raised — our team will get back to you`);
      setOpen(false);
      setForm({ title: "", description: "", priority: "Medium", category: "Technical Issue" });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setSaving(true);
    try {
      await api.post(`/support/tickets/${active.id}/replies`, { body: reply.trim() });
      setReply("");
      openTicket(active);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  if (!tickets) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="support-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Support</h1>
          <p className="text-sm text-muted-foreground">Raise a technical support ticket and track responses from the ProcDeal team.</p>
        </div>
        <Button data-testid="raise-ticket-btn" className="bg-slate-900 hover:bg-slate-800" onClick={() => setOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Raise Ticket
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="overflow-hidden rounded-lg border bg-card" data-testid="ticket-list">
          <div className="border-b px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">My Tickets ({tickets.length})</div>
          <div className="max-h-[560px] divide-y overflow-y-auto">
            {tickets.length === 0 && (
              <div className="p-8 text-center">
                <LifeBuoy className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 text-sm text-muted-foreground">No tickets yet.</p>
              </div>
            )}
            {tickets.map((t) => (
              <button key={t.id} data-testid={`ticket-row-${t.ticket_number}`} onClick={() => openTicket(t)}
                className={`block w-full px-4 py-3 text-left transition-colors hover:bg-slate-50 ${active?.id === t.id ? "bg-slate-100" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-semibold">{t.title}</p>
                  <span data-testid={`ticket-status-${t.ticket_number}`} className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[t.status]}`}>{t.status}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  <span className="font-mono">{t.ticket_number}</span> · {t.category} · <span className={PRIORITY_STYLES[t.priority]}>{t.priority}</span>
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border bg-card lg:col-span-2" data-testid="ticket-detail">
          {active ? (
            <>
              <div className="border-b px-5 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-mono text-xs text-muted-foreground">{active.ticket_number}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[active.status]}`}>{active.status}</span>
                  <span className={`text-xs ${PRIORITY_STYLES[active.priority]}`}>{active.priority}</span>
                  <span className="text-xs text-muted-foreground">{active.category}</span>
                </div>
                <h2 className="mt-1 font-heading text-lg font-semibold" data-testid="ticket-detail-title">{active.title}</h2>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700" data-testid="ticket-detail-description">{active.description}</p>
                <p className="mt-2 text-xs text-muted-foreground">Raised {fmtDateTime(active.created_at)}{active.assigned_admin ? ` · Assigned to ${active.assigned_admin}` : ""}</p>
              </div>
              <div className="max-h-[380px] space-y-3 overflow-y-auto p-5" data-testid="ticket-replies">
                {active.replies.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No responses yet.</p>}
                {active.replies.map((r) => {
                  const isAdmin = r.by_role === "org_admin";
                  return (
                    <div key={r.id} className={`rounded-lg border p-3.5 ${isAdmin ? "border-blue-200 bg-blue-50/50" : "bg-white"}`} data-testid={`ticket-reply-${r.id.slice(0, 8)}`}>
                      <p className="text-xs font-semibold">{r.by_name} <span className="font-normal text-muted-foreground">· {isAdmin ? "ProcDeal Support" : r.by_role.replace("_", " ")} · {fmtDateTime(r.created_at)}</span></p>
                      <p className="mt-1 whitespace-pre-wrap text-sm">{r.body}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 border-t p-3">
                <input data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), sendReply())}
                  placeholder="Type your reply..." className="flex-1 rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                <Button data-testid="ticket-reply-send" disabled={saving || !reply.trim()} onClick={sendReply} className="bg-slate-900 hover:bg-slate-800">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <LifeBuoy className="h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm text-muted-foreground">Select a ticket to view the conversation.</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="ticket-dialog">
          <DialogHeader><DialogTitle className="font-heading">Raise Ticket</DialogTitle></DialogHeader>
          <form onSubmit={raise} className="space-y-4">
            <div>
              <Label>Issue Title *</Label>
              <Input data-testid="ticket-title-input" required className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Issue Description *</Label>
              <textarea data-testid="ticket-desc-input" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <select data-testid="ticket-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Priority</Label>
                <select data-testid="ticket-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm">
                  {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <Button type="submit" data-testid="ticket-submit-btn" disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Raise Ticket
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
